import envConfig from "../env.config";

interface PeliasSearchParams {
  text: string;
  focus?: {
    point: {
      lat: number;
      lon: number;
    };
  };
  sources?: string;
  layers?: string;
  size?: number;
  lang?: string;
}

interface PeliasReverseParams {
  point: {
    lat: number;
    lon: number;
  };
  size?: number;
  lang?: string;
}

interface PeliasSearchResult {
  type: string;
  properties: {
    id: string;
    gid: string;
    layer: string;
    source: string;
    source_id: string;
    name: string;
    housenumber?: string;
    street?: string;
    neighbourhood?: string;
    locality?: string;
    borough?: string;
    county?: string;
    region?: string;
    country?: string;
    country_a?: string;
    postcode?: string;
    confidence?: number;
    match_type?: string;
    accuracy?: string;
    label?: string;
  };
  geometry: {
    type: string;
    coordinates: number[]; // [lng, lat] format
  };
  bbox?: number[];
}

// Pelias result format
export interface PeliasResult {
  place_id: number;
  gid?: string;
  licence: string;
  osm_type: string;
  osm_id: number;
  boundingbox: string[];
  lat: string;
  lon: string;
  display_name: string;
  class: string;
  type: string;
  importance: number;
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
    neighbourhood?: string;
    village?: string;
  };
}

const convertPeliastoJSON = (
  peliasResult: PeliasSearchResult
): PeliasResult => {
  const [lng, lat] = peliasResult.geometry.coordinates;
  const props = peliasResult.properties;

  return {
    place_id: parseInt(props.id) || Math.random(),
    gid: props.gid,
    licence: "Pelias",
    osm_type: props.layer,
    osm_id: parseInt(props.source_id) || Math.random(),
    boundingbox: peliasResult.bbox?.map(String) || [],
    lat: lat.toString(),
    lon: lng.toString(),
    display_name: props.label || props.name,
    class: props.layer,
    type: props.layer,
    importance: props.confidence || 0.5,
    address: {
      house_number: props.housenumber,
      road: props.street,
      suburb: props.neighbourhood,
      city: props.locality,
      state: props.region,
      postcode: props.postcode,
      country: props.country,
      country_code: props.country_a,
      neighbourhood: props.neighbourhood,
      village: props.locality,
    },
  };
};

// export const peliasSearch = async (
//   params: PeliasSearchParams
// ): Promise<PeliasResult[]> => {
//   const searchParams = new URLSearchParams({
//     text: params.text,
//     size: (params.size || 10).toString(),
//     ...(params.focus && {
//       "focus.point.lat": params.focus.point.lat.toString(),
//       "focus.point.lon": params.focus.point.lon.toString(),
//     }),
//     ...(params.sources && { sources: params.sources }),
//     ...(params.layers && { layers: params.layers }),
//     ...(params.lang && { lang: params.lang }),
//   });

//   const response = await fetch(
//     `${envConfig.peliasApiUrl}/search?${searchParams.toString()}`,
//     {
//       headers: {
//         "User-Agent": "PrepitsApp/1.0",
//       },
//     }
//   );

//   if (!response.ok) {
//     throw new Error(`Pelias search failed: ${response.statusText}`);
//   }

//   const data = await response.json();

//   return data.features?.map(convertPeliastoJSON) || [];
// };

export const peliasReverseGeocode = async (
  params: PeliasReverseParams
): Promise<PeliasResult> => {
  const searchParams = new URLSearchParams({
    "point.lat": params.point.lat.toString(),
    "point.lon": params.point.lon.toString(),
    size: (params.size || 1).toString(),
    ...(params.lang && { lang: params.lang }),
  });

  const response = await fetch(
    `${envConfig.peliasApiUrl}/reverse?${searchParams.toString()}`,
    {
      headers: {
        "User-Agent": "PrepitsApp/1.0",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Pelias reverse geocoding failed: ${response.statusText}`);
  }

  const data = await response.json();
  const feature = data.features?.[0];

  return convertPeliastoJSON(feature);
};

export const peliasAutocomplete = async (
  params: PeliasSearchParams
): Promise<PeliasResult[]> => {
  const searchParams = new URLSearchParams({
    text: params.text,
    size: (params.size || 10).toString(),
    ...(params.focus && {
      "focus.point.lat": params.focus.point.lat.toString(),
      "focus.point.lon": params.focus.point.lon.toString(),
    }),
    ...(params.sources && { sources: params.sources }),
    ...(params.layers && { layers: params.layers }),
    ...(params.lang && { lang: params.lang }),
  });

  const response = await fetch(
    `${envConfig.peliasApiUrl}/autocomplete?${searchParams.toString()}`,
    {
      headers: {
        "User-Agent": "PrepitsApp/1.0",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Pelias autocomplete failed: ${response.statusText}`);
  }

  const data = await response.json();

  return data.features?.map(convertPeliastoJSON) || [];
};
